import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Download, FileText, Loader2, Trash2, Upload } from "lucide-react";

import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/utils/datetime";

type Doc = { id: string; filename: string; mime_type: string; size_bytes: number; shared_with_parent: boolean; created_at: string };
type Limits = { max_bytes: number; allowed_mime: string[] };

function fmtSize(n: number) {
    return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}

export function DocumentsSection({ entityType, entityId, isStudent = false }: { entityType: "person" | "employee"; entityId: string; isStudent?: boolean }) {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    // UI gate = documents.* permission AND the permission the backend endpoint enforces.
    const canView = hasPerm("documents.view") && hasPerm("people.view");
    const canUpload = hasPerm("documents.upload") && hasPerm("people.edit");
    const canDelete = hasPerm("documents.delete") && hasPerm("people.archive");
    const canShare = hasPerm("documents.share") && hasPerm("people.edit");
    const [docs, setDocs] = useState<Doc[]>([]);
    const [limits, setLimits] = useState<Limits | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [fileErr, setFileErr] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const load = () => api.get("/documents", { params: { entity_type: entityType, entity_id: entityId } })
        .then(r => setDocs(r.data.items || [])).catch(e => toast.error(formatApiError(e)));
    useEffect(() => {
        if (!canView) return;
        load();
        api.get("/documents/limits").then(r => setLimits(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityType, entityId, canView]);

    if (!canView) return null;

    const pick = (f: File | null) => {
        setFile(f); setFileErr(null);
        if (!f || !limits) return;
        if (f.size > limits.max_bytes) setFileErr(t("docs.too_large", { max: fmtSize(limits.max_bytes) }));
        else if (!limits.allowed_mime.includes(f.type)) setFileErr(t("docs.bad_type"));
    };

    const upload = async () => {
        if (!file || fileErr) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file); fd.append("entity_type", entityType); fd.append("entity_id", entityId);
            await api.post("/documents/upload", fd);
            toast.success(t("docs.uploaded"));
            setFile(null); if (inputRef.current) inputRef.current.value = "";
            load();
        } catch (e: any) { toast.error(`${t("docs.upload_failed")} ${formatApiError(e) || ""}`.trim()); }
        finally { setBusy(false); }
    };

    const download = async (d: Doc) => {
        try {
            const r = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a"); a.href = url; a.download = d.filename;
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (e: any) { toast.error(formatApiError(e)); }
    };
    const remove = async (d: Doc) => {
        if (!window.confirm(t("docs.delete_confirm", { name: d.filename }))) return;
        try { await api.delete(`/documents/${d.id}`); toast.success(t("docs.deleted")); load(); }
        catch (e: any) { toast.error(formatApiError(e)); }
    };
    const share = async (d: Doc, v: boolean) => {
        try { await api.post(`/documents/${d.id}/share`, { shared_with_parent: v }); load(); }
        catch (e: any) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="surface-card p-5 space-y-4" data-testid="documents-section">
            <h2 className="text-base font-semibold" style={{ color: "var(--text)" }}>{t("docs.title")}</h2>
            {canUpload && (
                <div className="flex flex-wrap items-center gap-3">
                    <input ref={inputRef} type="file" data-testid="documents-file-input" className="text-sm"
                        accept={limits?.allowed_mime.join(",")} onChange={e => pick(e.target.files?.[0] || null)} />
                    <button type="button" data-testid="documents-upload-btn" onClick={upload} disabled={!file || !!fileErr || busy}
                        className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                        style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {t("docs.upload")}
                    </button>
                    {fileErr && <span className="text-xs" style={{ color: "var(--error-fg)" }} data-testid="documents-file-error">{fileErr}</span>}
                </div>
            )}
            {docs.length === 0 ? <div className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="documents-empty">{t("docs.empty")}</div> : (
                <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                    {docs.map(d => (
                        <li key={d.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`document-row-${d.id}`}>
                            <FileText size={16} style={{ color: "var(--text-muted)" }} />
                            <span className="flex-1 min-w-0 truncate" style={{ color: "var(--text)" }}>{d.filename}</span>
                            <span className="text-xs" style={{ color: "var(--text-muted)" }}>{fmtSize(d.size_bytes)} · {fmtDateTime(d.created_at)}</span>
                            {isStudent && canShare && (
                                <label className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--text-muted)" }}>
                                    <input type="checkbox" checked={d.shared_with_parent} data-testid={`document-share-${d.id}`}
                                        onChange={e => share(d, e.target.checked)} /> {t("docs.share_with_parent")}
                                </label>
                            )}
                            <button type="button" onClick={() => download(d)} title={t("docs.download")} data-testid={`document-download-${d.id}`}
                                className="p-1.5 rounded-md hover:bg-[var(--input)]" style={{ color: "var(--text-muted)" }}><Download size={15} /></button>
                            {canDelete && (
                                <button type="button" onClick={() => remove(d)} title={t("docs.delete")} data-testid={`document-delete-${d.id}`}
                                    className="p-1.5 rounded-md hover:bg-[var(--input)]" style={{ color: "var(--text-muted)" }}><Trash2 size={15} /></button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
